import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  VarianteProdutoCardapioConsultaDTO,
  VarianteProdutoCardapioDTO,
} from '../../../../../../../models/cardapio-minishop/cardapio-minishop.model';

@Component({
  selector: 'app-variante-secao',
  templateUrl: './variante-secao.component.html',
  styleUrls: ['./variante-secao.component.scss'],
})
export class VarianteSecaoComponent implements OnInit, OnDestroy {

  @Input() variantes: VarianteProdutoCardapioConsultaDTO[] = [];
  @Input() produtoId = 0;
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Input() loading = false;

  @Output() criarVariante = new EventEmitter<VarianteProdutoCardapioDTO>();
  @Output() atualizarVariante = new EventEmitter<{ id: number; dto: VarianteProdutoCardapioDTO }>();
  @Output() removerVariante = new EventEmitter<number>();
  @Output() reordenarVariantes = new EventEmitter<number[]>();
  @Output() toggleAtivoVariante = new EventEmitter<{ id: number; ativo: boolean }>();

  // Estado interno
  varianteExpandida: number | null = null;
  novaVariante: Partial<VarianteProdutoCardapioDTO> | null = null;

  texts = {
    titulo: '',
    adicionar: '',
    nome: '',
    preco: '',
    preco_promocional: '',
    custo: '',
    desconto: '',
    sku: '',
    disponivel: '',
    indisponivel: '',
    expandir: '',
    recolher: '',
    nenhuma: '',
    confirmar_excluir: '',
    salvar: '',
    cancelar: '',
    excluir: '',
  };

  private langChangeSub!: Subscription;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
  }

  // --- Acoes ---

  toggleExpandir(id: number): void {
    this.varianteExpandida = this.varianteExpandida === id ? null : id;
  }

  iniciarNovaVariante(): void {
    this.novaVariante = {
      nome: '',
      preco: 0,
      precoPromocional: null,
      ordem: this.variantes.length,
      ativo: true,
      custo: null,
      desconto: null,
      sku: null,
    };
  }

  cancelarNovaVariante(): void {
    this.novaVariante = null;
  }

  salvarNovaVariante(): void {
    if (!this.novaVariante || !this.novaVariante.nome?.trim()) return;

    const dto: VarianteProdutoCardapioDTO = {
      nome: this.novaVariante.nome!.trim(),
      preco: this.novaVariante.preco || 0,
      precoPromocional: this.novaVariante.precoPromocional || null,
      ordem: this.novaVariante.ordem || this.variantes.length,
      produtoId: this.produtoId,
      ativo: this.novaVariante.ativo !== false,
      custo: this.novaVariante.custo || null,
      desconto: this.novaVariante.desconto || null,
      sku: this.novaVariante.sku || null,
    };

    this.criarVariante.emit(dto);
    this.novaVariante = null;
  }

  salvarEdicao(variante: VarianteProdutoCardapioConsultaDTO): void {
    const dto: VarianteProdutoCardapioDTO = {
      nome: variante.nome,
      preco: variante.preco,
      precoPromocional: variante.precoPromocional || null,
      ordem: variante.ordem,
      produtoId: variante.produtoId,
      ativo: variante.ativo,
      custo: variante.custo || null,
      desconto: variante.desconto || null,
      sku: variante.sku || null,
    };
    this.atualizarVariante.emit({ id: variante.id, dto });
    this.varianteExpandida = null;
  }

  onRemover(id: number): void {
    this.removerVariante.emit(id);
  }

  onToggleAtivo(variante: VarianteProdutoCardapioConsultaDTO): void {
    const novoAtivo = !variante.ativo;
    this.toggleAtivoVariante.emit({ id: variante.id, ativo: novoAtivo });
  }

  onDrop(event: CdkDragDrop<VarianteProdutoCardapioConsultaDTO[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.variantes, event.previousIndex, event.currentIndex);
    const ids = this.variantes.map(v => v.id);
    this.reordenarVariantes.emit(ids);
  }

  trackById(index: number, item: VarianteProdutoCardapioConsultaDTO): number {
    return item.id;
  }

  private loadTranslations(): void {
    const base = 'cardapio_minishop.variante';
    this.texts = {
      titulo: this.translate.instant(`${base}.titulo`),
      adicionar: this.translate.instant(`${base}.adicionar`),
      nome: this.translate.instant(`${base}.nome`),
      preco: this.translate.instant(`${base}.preco`),
      preco_promocional: this.translate.instant(`${base}.preco_promocional`),
      custo: this.translate.instant(`${base}.custo`),
      desconto: this.translate.instant(`${base}.desconto`),
      sku: this.translate.instant(`${base}.sku`),
      disponivel: this.translate.instant(`${base}.disponivel`),
      indisponivel: this.translate.instant(`${base}.indisponivel`),
      expandir: this.translate.instant(`${base}.expandir`),
      recolher: this.translate.instant(`${base}.recolher`),
      nenhuma: this.translate.instant(`${base}.nenhuma`),
      confirmar_excluir: this.translate.instant(`${base}.confirmar_excluir`),
      salvar: this.translate.instant('cardapio_minishop.comum.salvar'),
      cancelar: this.translate.instant('cardapio_minishop.comum.cancelar'),
      excluir: this.translate.instant('cardapio_minishop.comum.excluir'),
    };
  }
}
